import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ForCompanies from "@/components/ForCompanies";
import ForConsultants from "@/components/ForConsultants";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { siteVisibility } from "@/config/siteVisibility";

// Lazy load below-the-fold components
const ClientsSection = lazy(() => import("@/components/ClientsSection"));
const ProjectsSection = lazy(() => import("@/components/ProjectsSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const AboutSection = lazy(() => import("@/components/AboutSection"));
const WhyChooseSection = lazy(() => import("@/components/WhyChooseSection"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const TeamSection = lazy(() => import("@/components/TeamSection"));
const MissionSection = lazy(() => import("@/components/MissionSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const PartnersSection = lazy(() => import("@/components/PartnersSection"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

// Loading placeholder
const SectionLoader = () => (
  <div className="py-20 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "New Data Frontier",
  "description": "Marktleider in data, AI en tech consultancy. Expert consultants van starter tot senior niveau voor bedrijven in heel Nederland.",
  "url": "https://newdatafrontier.nl",
  "logo": "https://newdatafrontier.nl/ndf-logo.png",
  "telephone": "+31639754532",
  "email": "info@newdatafrontier.nl",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Groot Handelsgebouw, 4e etage",
    "addressLocality": "Rotterdam",
    "addressCountry": "NL"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "51.9225",
    "longitude": "4.4792"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Nederland"
  },
  "serviceType": ["Data Consultancy", "AI Consultancy", "Tech Consultancy", "Detachering", "Software Development"],
  "priceRange": "€60-€80 per uur",
  "sameAs": [
    "https://www.linkedin.com/company/datastudent"
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "100"
  }
};

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Data, AI & Tech Consultancy Nederland"
        description="Expert data, AI en tech consultants van starter tot senior. 250+ professionals, 100+ klanten. Vaak binnen een maand gestart. Ontdek onze drie merken."
        canonicalPath="/"
        keywords="data consultants, AI experts, tech consultancy, detachering, data engineering, machine learning, Rotterdam, Nederland, New Data Frontier"
        structuredData={organizationSchema}
      />
      <Header />
      <main>
        <Hero />
        {siteVisibility.homeSections.forCompanies && <ForCompanies />}
        {siteVisibility.homeSections.whyChoose && (
          <Suspense fallback={<SectionLoader />}>
            <WhyChooseSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.forConsultants && <ForConsultants />}
        {siteVisibility.homeSections.clients && (
          <Suspense fallback={<SectionLoader />}>
            <ClientsSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.projects && (
          <Suspense fallback={<SectionLoader />}>
            <ProjectsSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.testimonials && (
          <Suspense fallback={<SectionLoader />}>
            <TestimonialsSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.about && (
          <Suspense fallback={<SectionLoader />}>
            <AboutSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.pricing && (
          <Suspense fallback={<SectionLoader />}>
            <PricingSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.mission && (
          <Suspense fallback={<SectionLoader />}>
            <MissionSection />
          </Suspense>
        )}
        {siteVisibility.homeSections.team && (
          <Suspense fallback={<SectionLoader />}>
            <TeamSection />
          </Suspense>
        )}
      </main>
      {siteVisibility.homeSections.partners && (
        <Suspense fallback={null}>
          <PartnersSection />
        </Suspense>
      )}
      {siteVisibility.homeSections.contact && (
        <Suspense fallback={<SectionLoader />}>
          <ContactSection />
        </Suspense>
      )}
      <Footer />
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>
  );
};

export default Index;
